import { Building2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SearchableCombobox, type ComboboxOption } from '@/components/ui/searchable-combobox';

interface OrganizationFilterProps {
  selectedOrg: string;
  onOrgChange: (orgId: string) => void;
  showAllOption?: boolean;
  className?: string;
}

export function OrganizationFilter({
  selectedOrg,
  onOrgChange,
  showAllOption = true,
  className = 'w-[280px]',
}: OrganizationFilterProps) {
  const { currentRole } = useAuth();
  const isSuperAdmin = currentRole === 'super_admin';
  // El Auditor también filtra por empresa, pero la RLS solo le devuelve las
  // organizaciones de sus cohortes asignadas (no todas).
  const canFilter = isSuperAdmin || currentRole === 'auditor';

  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations-filter'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, logo_url')
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: canFilter,
  });

  if (!canFilter) {
    return null;
  }

  const options: ComboboxOption[] = [
    ...(showAllOption
      ? [
          {
            value: 'all',
            label: 'Todas las organizaciones',
            iconNode: <span aria-hidden>🌐</span>,
            keywords: ['todas', 'todos', 'all'],
          },
        ]
      : []),
    ...organizations.map((org) => ({
      value: org.id,
      label: org.name,
      iconUrl: org.logo_url || undefined,
      iconNode: org.logo_url ? undefined : <Building2 className="w-4 h-4 opacity-70" />,
    })),
  ];

  return (
    <SearchableCombobox
      options={options}
      value={selectedOrg}
      onSelect={onOrgChange}
      placeholder="Seleccionar organización"
      searchPlaceholder="Buscar organización..."
      emptyText="Sin organizaciones"
      triggerClassName={className}
      triggerIcon={<Building2 className="w-4 h-4" />}
    />
  );
}

// Hook to get the organization context for filtering data
export function useOrganizationFilter(selectedOrg: string) {
  const { currentRole, profile } = useAuth();
  const isSuperAdmin = currentRole === 'super_admin';
  const isAuditor = currentRole === 'auditor';
  // Super admin y auditor eligen empresa libremente (el auditor, entre las de sus
  // cohortes asignadas, garantizado por RLS). El admin de empresa queda fijado a la suya.
  const canSelectOrg = isSuperAdmin || isAuditor;

  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations-filter'],
    queryFn: async () => {
      const { data, error } = await supabase.from('organizations').select('id, name').order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: isSuperAdmin || currentRole === 'admin' || isAuditor,
  });

  const effectiveOrg = canSelectOrg ? selectedOrg : (profile?.organization_id || '');
  const showAllOrgs = canSelectOrg && selectedOrg === 'all';

  const getOrgName = () => {
    if (showAllOrgs) return 'Todas las organizaciones';
    const org = organizations.find((o) => o.id === effectiveOrg);
    return org?.name || '';
  };

  return {
    isSuperAdmin,
    effectiveOrg,
    showAllOrgs,
    getOrgName,
    organizations,
  };
}
